import React, { useState, useRef, useEffect } from 'react';
import Icon from '@salesforce/design-system-react/components/icon';
import { colors, type, space, radius, motion } from '../tokens';

const InlineEditField = ({
  label,
  value: initialValue = '',
  onSave,
  multiline = false,
  isLink = false,
  placeholder = '—',
  renderValue,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [hover, setHover] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select && inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => { setValue(initialValue); }, [initialValue]);

  const save = () => {
    setEditing(false);
    if (onSave && value !== initialValue) onSave(value);
  };
  const cancel = () => {
    setValue(initialValue);
    setEditing(false);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !multiline) { e.preventDefault(); save(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  const InputEl = multiline ? 'textarea' : 'input';

  const displayValue = renderValue ? renderValue(value) : (value || placeholder);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ marginBottom: space[4], position: 'relative' }}
    >
      {label && (
        <div style={{ ...type.label, color: colors.textTertiary, marginBottom: space[1] }}>
          {label}
        </div>
      )}
      {!editing ? (
        <div
          onClick={() => setEditing(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: space[2],
            padding: `${space[1]}px ${space[2]}px`,
            margin: `-${space[1]}px -${space[2]}px`,
            borderRadius: radius.sm,
            cursor: 'pointer',
            background: hover ? colors.surfaceInk : 'transparent',
            transition: `background ${motion.fast}`,
            minHeight: 24,
          }}
        >
          <span style={{
            ...type.body,
            color: value
              ? (isLink ? colors.brand : colors.textPrimary)
              : colors.textTertiary,
            fontStyle: value ? 'normal' : 'italic',
          }}>
            {displayValue}
          </span>
          <Icon
            category="utility"
            name="edit"
            size="xx-small"
            style={{
              fill: colors.textTertiary,
              opacity: hover ? 1 : 0,
              transition: `opacity ${motion.fast}`,
              flexShrink: 0,
            }}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: space[1] }}>
          <InputEl
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKey}
            rows={multiline ? 3 : undefined}
            style={{
              flex: 1,
              padding: `${space[1]}px ${space[2]}px`,
              border: `1px solid ${colors.brand}`,
              borderRadius: radius.sm,
              ...type.body,
              color: colors.textPrimary,
              fontFamily: 'inherit',
              outline: 'none',
              resize: multiline ? 'vertical' : 'none',
              background: colors.surface,
            }}
          />
          <button
            onClick={save}
            title="Save (Enter)"
            style={{
              padding: 4,
              background: colors.success,
              border: 'none',
              borderRadius: radius.sm,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon category="utility" name="check" size="xx-small" style={{ fill: '#fff' }} />
          </button>
          <button
            onClick={cancel}
            title="Cancel (Esc)"
            style={{
              padding: 4,
              background: colors.surfaceInk,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon category="utility" name="close" size="xx-small" style={{ fill: colors.textSecondary }} />
          </button>
        </div>
      )}
    </div>
  );
};

export default InlineEditField;
